<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('balanceBanks.title')" :description="t('balanceBanks.description')">
      <template #actions>
        <AppButton @click="showAddBankModal = true">
          <Plus class="h-4 w-4 mr-2" />
          {{ t('balanceBanks.addBank') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Stat cards -->
    <div v-if="statistics" class="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <AppStatCard :label="t('balanceBanks.totalBanks')" :value="statistics.totals.total_banks" :icon="Layers" iconBg="bg-blue-50" iconColor="text-blue-600" :loading="loading" />
      <AppStatCard :label="t('balanceBanks.totalAllocated')" :value="`${Math.round(statistics.totals.total_allocated_days)} ${t('balanceBanks.days')}`" :icon="Calendar" iconBg="bg-emerald-50" iconColor="text-emerald-600" :loading="loading" />
      <AppStatCard :label="t('balanceBanks.totalUsed')" :value="`${Math.round(statistics.totals.total_used_days)} ${t('balanceBanks.days')}`" :icon="CheckCircle" iconBg="bg-amber-50" iconColor="text-amber-600" :loading="loading" />
      <AppStatCard :label="t('balanceBanks.totalRemaining')" :value="`${Math.round(statistics.totals.total_remaining_days)} ${t('balanceBanks.days')}`" :icon="TrendingUp" iconBg="bg-purple-50" iconColor="text-purple-600" :loading="loading" />
    </div>

    <!-- Banks list -->
    <AppCard>
      <div class="px-4 py-3 border-b border-border">
        <p class="text-sm font-medium text-foreground">{{ t('balanceBanks.title') }}</p>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-12">
        <AppSpinner size="lg" />
      </div>

      <AppEmptyState v-else-if="banks.length === 0" :icon="Layers" :title="t('balanceBanks.noBalanceBanks')" :description="t('balanceBanks.noBalanceBanksDesc')" />

      <div v-else class="divide-y divide-border">
        <div
          v-for="bank in banks"
          :key="bank.id"
          class="p-4 hover:bg-muted/30 cursor-pointer transition-colors"
          @click="selectBank(bank)"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1 min-w-0">
              <!-- Name + badges -->
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <div class="h-3 w-3 rounded-full shrink-0" :style="{ backgroundColor: bank.color || '#3B82F6' }"></div>
                <span class="text-sm font-semibold text-foreground">{{ bank.name }}</span>
                <span class="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">{{ bank.code }}</span>
                <AppBadge :variant="bank.calculation_type === 'tenure_based' ? 'default' : bank.calculation_type === 'fixed' ? 'success' : 'warning'">
                  {{ formatBankType(bank.calculation_type) }}
                </AppBadge>
                <AppBadge v-if="bank.is_system_defined" variant="secondary">{{ t('balanceBanks.system') }}</AppBadge>
              </div>

              <p v-if="bank.description" class="text-xs text-muted-foreground mb-3">{{ bank.description }}</p>

              <!-- Stats grid -->
              <div class="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs mb-3">
                <div><p class="text-muted-foreground">{{ t('balanceBanks.employees') }}</p><p class="font-semibold text-foreground">{{ bank.employee_count || 0 }}</p></div>
                <div><p class="text-muted-foreground">{{ t('balanceBanks.allocated') }}</p><p class="font-semibold text-foreground">{{ Math.round(bank.total_allocated || 0) }}d</p></div>
                <div><p class="text-muted-foreground">{{ t('balanceBanks.used') }}</p><p class="font-semibold text-foreground">{{ Math.round(bank.total_used || 0) }}d</p></div>
                <div><p class="text-muted-foreground">{{ t('balanceBanks.remaining') }}</p><p class="font-semibold text-foreground">{{ Math.round(bank.total_remaining || 0) }}d</p></div>
                <div><p class="text-muted-foreground">{{ t('balanceBanks.leaveTypes') }}</p><p class="font-semibold text-foreground">{{ bank.leave_type_count || 0 }}</p></div>
              </div>

              <!-- Progress bar -->
              <div>
                <div class="flex justify-between text-[10px] text-muted-foreground mb-1">
                  <span>{{ t('balanceBanks.usage') }}</span>
                  <span>{{ getUsagePercentage(bank) }}%</span>
                </div>
                <div class="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                  <div class="h-full rounded-full transition-all" :class="getUsageColorClass(getUsagePercentage(bank))" :style="{ width: Math.min(getUsagePercentage(bank), 100) + '%' }"></div>
                </div>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="flex items-center gap-1 shrink-0">
              <AppButton size="icon-sm" variant="ghost" :title="t('balanceBanks.edit')" @click.stop="editBank(bank)">
                <Edit class="h-3.5 w-3.5" />
              </AppButton>
              <AppButton v-if="bank.calculation_type === 'tenure_based'" size="icon-sm" variant="ghost" class="text-emerald-600" title="Configure Tenure Rules" @click.stop="configureTenureRules(bank)">
                <Settings class="h-3.5 w-3.5" />
              </AppButton>
              <AppButton size="icon-sm" variant="ghost" class="text-purple-600" title="Manage Leave Types" @click.stop="manageLeaveTypes(bank)">
                <List class="h-3.5 w-3.5" />
              </AppButton>
            </div>
          </div>
        </div>
      </div>
    </AppCard>

    <!-- Child modals (unchanged) -->
    <BankDetailsModal v-if="selectedBank" :bank="selectedBank" @close="selectedBank = null" @refresh="loadData" />
    <BankFormModal v-if="showAddBankModal || editingBank" :bank="editingBank" @close="closeFormModal" @saved="handleBankSaved" />
    <TenureRulesModal v-if="tenureRulesBank" :bank="tenureRulesBank" @close="tenureRulesBank = null" @saved="loadData" />
    <LeaveTypesModal v-if="leaveTypesBank" :bank="leaveTypesBank" @close="leaveTypesBank = null" @saved="loadData" />

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { Plus, Layers, Calendar, CheckCircle, TrendingUp, Edit, Settings, List } from 'lucide-vue-next';
import { AppPageHeader, AppStatCard, AppCard, AppBadge, AppButton, AppSpinner, AppEmptyState } from '@/components/ui';
import balanceBankService from '@/services/balanceBankService';
import BankDetailsModal from '@/components/admin/balanceBanks/BankDetailsModal.vue';
import BankFormModal from '@/components/admin/balanceBanks/BankFormModal.vue';
import TenureRulesModal from '@/components/admin/balanceBanks/TenureRulesModal.vue';
import LeaveTypesModal from '@/components/admin/balanceBanks/LeaveTypesModal.vue';

const { t } = useI18n();

// State
const loading = ref(false);
const banks = ref([]);
const statistics = ref(null);
const selectedBank = ref(null);
const showAddBankModal = ref(false);
const editingBank = ref(null);
const tenureRulesBank = ref(null);
const leaveTypesBank = ref(null);

// Load data on mount
onMounted(() => {
  loadData();
});

// Load banks and statistics
async function loadData() {
  loading.value = true;
  try {
    const [banksResponse, statsResponse] = await Promise.all([
      balanceBankService.getAllBanks(),
      balanceBankService.getStatistics()
    ]);

    banks.value = banksResponse.data;
    statistics.value = statsResponse.data;
  } catch (error) {
    console.error('Error loading balance banks:', error);
    // Show error notification
  } finally {
    loading.value = false;
  }
}

// Select bank to view details
function selectBank(bank) {
  selectedBank.value = bank;
}

// Edit bank
function editBank(bank) {
  editingBank.value = bank;
}

// Configure tenure rules
function configureTenureRules(bank) {
  tenureRulesBank.value = bank;
}

// Manage leave types
function manageLeaveTypes(bank) {
  leaveTypesBank.value = bank;
}

// Close form modal
function closeFormModal() {
  showAddBankModal.value = false;
  editingBank.value = null;
}

// Handle bank saved
function handleBankSaved() {
  closeFormModal();
  loadData();
}

// Get bank type badge class
function getBankTypeBadgeClass(type) {
  const classes = {
    tenure_based: 'bg-blue-100 text-blue-800',
    fixed: 'bg-green-100 text-green-800',
    manual: 'bg-orange-100 text-orange-800'
  };
  return classes[type] || 'bg-gray-100 text-gray-800';
}

// Format bank type
function formatBankType(type) {
  const labels = {
    tenure_based: t('balanceBanks.tenureBased'),
    fixed: t('balanceBanks.fixed'),
    manual: t('balanceBanks.flexible')
  };
  return labels[type] || type;
}

// Get usage percentage
function getUsagePercentage(bank) {
  if (!bank.total_allocated || bank.total_allocated === 0) return 0;
  return Math.round((bank.total_used / bank.total_allocated) * 100);
}

// Get usage color class
function getUsageColorClass(percentage) {
  if (percentage >= 90) return 'bg-red-500';
  if (percentage >= 70) return 'bg-amber-500';
  if (percentage >= 50) return 'bg-yellow-500';
  return 'bg-emerald-500';
}
</script>
